import classNames from 'classnames'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import HartuCancelationButton from '../../atoms/HartuCancelationButton/HartuCancelButton'
import PageRow from '../../atoms/PageRow/PageRow'
import SearchInput from '../../atoms/SearchInput/SearchInput'
import TimePicker from '../../atoms/TimePicker/TimePicker'
import CalendarModal from '../../components/CalendarModal/CalendarModal'
import HartuCategorySelector from '../../components/Hartu/HartuCategorySelector/HartuCategorySelector'
import HartuFacilityGrid from '../../components/Hartu/HartuFacilityGrid/HartuFacilityGrid'
import HartuPageIntro from '../../components/Hartu/HartuPageIntro/HartuPageIntro'
import HartuRemoveFiltersButton from '../../components/Hartu/HartuRemoveFiltersButton/HartuRemoveFiltersButton'
import HartuSearchInput from '../../components/Hartu/HartuSearchInput/HartuSearchInput'
import BODY_OVERFLOW_HIDDEN from '../../constants/styles'
import t from '../../i18n/translations/translations'
import { setSelectedHartu as setSelectedHartuAction } from '../../store/actions'
import hartuPageDataTransformer from '../../transformers/hartuPageDataTransformer'
import './HartuContainer.css'

function HartuContainer({ match }) {
    const dispatch = useDispatch()
    const {
        categories,
        facilitiesLoading,
        facilitiesAll,
        instanceID,
        pageData,
    } = useSelector(({ page, hartu, pages }) => ({
        categories: hartu?.hartu?.data?.categories,
        facilitiesLoading: hartu?.facilitiesLoading,
        facilitiesAll: hartu?.facilities,
        hartu: hartu?.hartu,
        instanceID: pages?.hartu?.data?.[0]?.idinstancia,
        page,
        pageData: pages?.hartu?.data?.[0],
    }))

    const setSelectedHartu = (payload) =>
        dispatch(setSelectedHartuAction(payload))

    const { lang } = match
    const [isModalOpened, setIsModalOpened] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedStartTime, setSelectedStartTime] = useState(null)
    const [selectedFinishTime, setSelectedFinishTime] = useState(null)
    const [searchValue, setSearchValue] = useState(null)
    const [facilitiesByTime, setFacilitiesByTime] = useState(null)
    const [isLoadingAvailability, setIsLoadingAvailability] = useState(false)

    const handleSearchAvailability = async () => {
        if (!instanceID || !selectedDate || !selectedStartTime || !selectedFinishTime) {
            setFacilitiesByTime(null);
            return
        }

        if (isLoadingAvailability) {
            return
        }

        setIsLoadingAvailability(true)

        const payload = {
            idinstancia: instanceID,
            fecha: selectedDate,
            hora_inicio: selectedStartTime,
            hora_fin: selectedFinishTime,
        }

        const url = `${process.env.REACT_APP_API_URL_BASE}hartu/frontend/available_facilities_with_dates`

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "x-api-key": sessionStorage.getItem('instancek'),
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                },
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                if (response.status === 401) {
                    sessionStorage.clear()
                    window.location.href = '/'
                    return
                }

                console.error('HTTP error:', response.status)
                setFacilitiesByTime(null)
                return
            }

            const data = await response.json()

            setFacilitiesByTime(data.data || [])
        } catch (error) {
            console.error('Fetch error (network / JSON):', error)
            setFacilitiesByTime(null)
        } finally {
            setIsLoadingAvailability(false)
        }
    }

    const today = () => {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        return d
    }

    const formatDate = (date) => {
        const yyyy = date.getFullYear()
        const mm = (date.getMonth() + 1).toString().padStart(2, '0')
        const dd = date.getDate().toString().padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
    }

    const now = today()
    const range = 200
    const availableDates = Array.from(
        { length: range },
        (_, i) => {
            const d = new Date(now)
            d.setDate(d.getDate() + i)
            return formatDate(d)
        }
    )

    const filterByCategory = (filteredFacilities) =>
        filteredFacilities?.filter(
            (facility) => facility.facility_type === selectedCategory
        )

    const filterBySearchValue = (filteredFacilities) =>
        filteredFacilities?.filter(
            (facility) =>
                facility.name.es.toLowerCase().includes(searchValue) ||
                facility.name.eu.toLowerCase().includes(searchValue)
        )

    const START_TIME = 'startTime'

    const FINISH_TIME = 'finishTime'

    useEffect(() => {
        if (instanceID && lang) {
            setSelectedHartu({
                instanceID,
                lang,
            })
        }
    }, [instanceID, lang])

    useEffect(() => {
        handleSearchAvailability()
    }, [selectedDate, selectedStartTime, selectedFinishTime, instanceID])

    const handleModal = () => {
        setIsModalOpened(!isModalOpened)
    }

    const handleTimeLostFocus = (event) => {
        const eventTarget = event.target
        const selectedTime = eventTarget.value

        if (!selectedTime) {
            eventTarget.value = null
        }
    }

    const handleTimeChange = (event) => {
        const selectedTimeValue = event.target.value
        const selectedTimeId = event.target.id

        if (selectedTimeId === START_TIME) {
            setSelectedStartTime(selectedTimeValue || null)
        }

        if (selectedTimeId === FINISH_TIME) {
            setSelectedFinishTime(selectedTimeValue || null)
        }

        if (
            selectedDate &&
            selectedStartTime &&        
            selectedFinishTime          
        ) {
            handleSearchAvailability()
        }
    }

    // Resetea valores de los TIMEPICKER por defecto
    useEffect(() => {
        setSelectedStartTime(null);
        setSelectedFinishTime(null);
    }, [selectedDate]); 

    const handleSearchChange = (event) => {
        const searchValueEvent = event.target.value.toLowerCase()

        setSearchValue(searchValueEvent)
    }

    const handleRemoveFilters = () => {
        setSelectedCategory(null)
        setSelectedDate(null)
        setSearchValue(null)
        setSelectedStartTime(null)
        setSelectedFinishTime(null)
        setFacilitiesByTime(null); 
    }
    
    const filteredFacilities = useMemo(() => {
        if (!facilitiesAll) return null

        let result = [...facilitiesAll]

        if (searchValue) {
            result = filterBySearchValue(result)
        }
        if (selectedCategory) {
            result = filterByCategory(result)
        }

        if (facilitiesByTime && facilitiesByTime.length > 0) {
            result = result.filter((facility) =>
                facilitiesByTime.includes(facility.id)
            )
        }
    
        return result
    }, [facilitiesAll, searchValue, selectedCategory, facilitiesByTime])

    const updateBreadcrumbLink = () => {
        const breadcrumbElement = document.querySelector('.breadcrumb .active')
        const linkText = breadcrumbElement.innerHTML

        breadcrumbElement.innerHTML = `<a href="">${linkText}</a>`
    }

    const filterFacilitiesByCategory = (category) => {
        updateBreadcrumbLink()
        setSelectedCategory(category)
    }

    const selectDate = (date) => {
        setSelectedDate(date.format('YYYY-MM-DD'))

        if (date) {
            handleModal()
            document.body.classList.remove(BODY_OVERFLOW_HIDDEN)
        }
    }

    const pageIntroData = pageData && hartuPageDataTransformer(pageData)
    const selectedCategoryObject = categories?.find(
        (category) => category.id === selectedCategory
    )
    return (
        <div className="hartuContainer">
            <div
                className={classNames('hartuContainer__top ', {
                    'hartuContainer__top--tall': selectedCategory,
                })}
            >
                <HartuPageIntro title={pageIntroData?.titulo}>
                    <PageRow>
                        {pageIntroData && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: pageIntroData.descripcion,
                                }}
                            />
                        )}
                    </PageRow>
                </HartuPageIntro>

                <PageRow>
                    <div className="hartuContainer__intro__subtitle">
                        {!selectedCategory && (
                            <p>{t.category_selector_title[lang]}</p>
                        )}
                        {selectedCategoryObject && (
                            <p>
                                {t.category_selected[lang]}{' '}
                                {lang === 'eu'
                                    ? selectedCategoryObject.name.eu
                                    : selectedCategoryObject.name.es}
                            </p>
                        )}
                    </div>
                    <div className="flex-items">
                        <HartuCategorySelector
                            action={filterFacilitiesByCategory}
                            categories={categories}
                            lang={lang}
                            selectedCategory={selectedCategory}
                        />

                        <SearchInput
                            initialData={filteredFacilities}
                            onChange={handleSearchChange}
                            placeholder={t.filter_selector[lang]}
                        />

                        <HartuSearchInput
                            lang={lang}
                            selectedDate={selectedDate}
                            openModal={handleModal}
                        />

                        {selectedDate && (
                            <>
                                <TimePicker
                                    id={START_TIME}
                                    name={START_TIME}
                                    value={selectedStartTime || ''}
                                    onBlur={handleTimeLostFocus}
                                    onChange={handleTimeChange}
                                />
                                <TimePicker
                                    id={FINISH_TIME}
                                    name={FINISH_TIME}
                                    value={selectedFinishTime || ''} 
                                    onBlur={handleTimeLostFocus}
                                    onChange={handleTimeChange}
                                />
                            </>
                        )}

                        <HartuRemoveFiltersButton
                            onClick={handleRemoveFilters}
                            lang={lang}
                        />
                    </div>
                </PageRow>
            </div>
            <PageRow className="hartuContainer__bottom">
                <HartuFacilityGrid
                    isLoading={facilitiesLoading}
                    selectedDate={selectedDate}
                    categories={categories}
                    data={filteredFacilities}
                    lang={lang}
                />
                <HartuCancelationButton lang={lang} />
            </PageRow>
            <CalendarModal
                availableDates={availableDates}
                isOpened={isModalOpened}
                handleModal={handleModal}
                selectDate={selectDate}
                lang={lang}
            />
        </div>
    )
}

export default HartuContainer
